import React, { useEffect, useRef, useState } from "react";

import { Button, IconButton, TextField } from "@mui/material";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "ag-grid-enterprise";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import EditIcon from "@mui/icons-material/Edit";

import SideNav from "component/layouts/sideNav";
import {
  setItemCategories,
  setItemList,
  setItems,
  setOrders,
  setSiteCategories,
  setSites,
  setUoms,
  setUsers,
  siteCellRenderer,
  userCellRenderer,
} from "component/cellRenderers/renderers";
import {
  setAlertConfirmDeletion,
  setAlertTitleText,
} from "component/modal/alertModal";

import {
  ADD,
  CUSTOMER_MANAGEMENT,
  DELETE,
  GO,
  INVOICE,
} from "resources/constant/strings/string";
import {
  LIGHT_GREY_BACKGROUND,
  PRUSSIAN_BLUE,
  SPANISH_GREEN,
  WHITE,
} from "resources/constant/colors/theme";
import {
  PATH_INVOICE,
  PATH_INVOICE_DETAIL_WITHOUT_PARAM,
  PATH_LOGIN,
} from "resources/constant/url/url";

import { APILOGIN } from "apihandler/login";
import { APIMASTER } from "apihandler/master";
import { APISALES } from "apihandler/sales";

import { setDates, setFilterModel, saveInvoicesToCache } from "store/Slice/invoicesSlice";
import { useDebounce } from "resources/constant/utilities/util";

const getCacheKey = (fromDate, toDate) =>
  `${fromDate.format("YYYY-MM-DD")}_${toDate.format("YYYY-MM-DD")}`;


export default function Invoice() {
  let { date } = useParams();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const gridRef = useRef();

  const { fromDate, toDate, filterModel, invoicesCache } = useSelector((s) => s.invoices);

  const [dateValue, setDateValue] = React.useState(null);
  const [toDateValue, setToDateValue] = React.useState(dayjs().endOf("month"));
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!APILOGIN.GetIsLoggedIn()) navigate(PATH_LOGIN);
  }, []);

  const [selectedRowId, setSelectedRowId] = useState(0);
  const [selectedRowIds, setSelectedRowIds] = useState([]);

  const deleteModel = () => {
    if (selectedRowIds.length === 0) return;

    setAlertConfirmDeletion(
      "Confirmation needed",
      `Do you confirm on proceeding to delete ${selectedRowIds.length} record(s)?`
    ).then((consent) => {
      if (!consent.isConfirmed) return;

      APISALES.DeleteInvoices(selectedRowIds)
        .then((resp) => resp.json())
        .then((data) => {
          if (!data.status) {
            setAlertTitleText(
              "Oops...",
              `Something went wrong...${data.message}`
            );
            return;
          }
          if (data.statusCode === 204) {
            const selectedData = gridRef.current.api.getSelectedRows();
            gridRef.current.api.applyTransaction({ remove: selectedData });
            setAlertTitleText("Success", "The record was deleted successfully");
          } else {
            setAlertTitleText(
              "Oops...",
              `Something went wrong...${data.message}`
            );
          }
        });
    });
  };

  const onRowSelectionChanged = (event) => {
    const selectedRows = event.api.getSelectedRows();
    if (selectedRows.length > 0) {
      setSelectedRowId(selectedRows[0].id);
      const ids = selectedRows.map((row) => row.id);
      setSelectedRowIds(ids);
    } else {
      setSelectedRowId(0);
      setSelectedRowIds([]);
    }
  };

  const columnDefs = [
    {
      flex: 1.7,
      headerName: "RA-Bill No",
      field: "invoiceNo",
      checkboxSelection: false,
      wrapText: true,
      autoHeight: true,
      cellStyle: { "border-right-color": "#e2e2e2" },
    },
    {
      headerName: "RA- Bill Date",
      field: "invoiceDate",
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellStyle: {
        "text-align": "center",
        "border-right-color": "#e2e2e2",
      },
    },
    {
      flex: 1.7,
      headerName: "Site Name",
      field: "customerId",
      autoHeight: true,
      valueGetter: (p) => siteCellRenderer({ value: p.data.customerId }),
      wrapText: true,
      cellStyle: {
        "border-right-color": "#e2e2e2",
      },
    },
    {
      headerName: "Amount",
      field: "totalSupplyAmount",
      filter: false,
      floatingFilter: false,
      cellStyle: {
        "text-align": "center",
        "border-right-color": "#e2e2e2",
      },
    },
    {
      headerName: "Inst Amount",
      field: "totalInstallationAmount",
      filter: false,
      floatingFilter: false,
      cellStyle: {
        "text-align": "center",
        "border-right-color": "#e2e2e2",
      },
    },
    {
      headerName: "Grand Total",
      field: "totalAmount",
      filter: false,
      floatingFilter: false,
      cellStyle: {
        "text-align": "center",
        "border-right-color": "#e2e2e2",
      },
    },
    {
      headerName: "Modified By",
      field: "modifiedBy",
      valueGetter: (p) => userCellRenderer({ value: p.data.modifiedBy }),
      cellStyle: { "border-right-color": "#e2e2e2" },
      autoHeight: true,
      wrapText: true,
    },
    {
      flex: 0.6,
      headerName: "Edit",
      field: "id",
      sortable: false,
      filter: false,
      floatingFilter: false,
      cellRenderer: (params) => (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          <IconButton
            aria-label="edit"
            onClick={() => {
              navigate(PATH_INVOICE_DETAIL_WITHOUT_PARAM + params.data.id, {
                state: { date: dayjs(dateValue).format("YYYY-MM-DD") },
              });
            }}
          >
            <EditIcon style={{ color: PRUSSIAN_BLUE, width: "20px" }} />
          </IconButton>
        </div>
      ),
    },
  ];

  const defaultColDef = {
    resizable: true,
    sortable: true,
    editable: false,
    flex: 1,
    filter: "agTextColumnFilter",
    filterParams: {
      caseSensitive: false,
      defaultOption: "startsWith",
    },
    floatingFilter: true,
  };

  const fetchInvoices = (d1, d2) => {
    return APISALES.SalesInvoices(
      d1.format("YYYY-MM-DD"),
      d2.format("YYYY-MM-DD")
    )
      .then((resp) => resp.json())
      .then((resp) => {
        if (resp.statusCode !== 200) {
          setAlertTitleText("Oops..", `An error occurred ${resp.message}`);
          return [];
        }
        return resp.response.sort((a, b) => b.id - a.id);
      });
  };

  const loadInvoices = async (dateValue, toDateValue) => {
  try {
    setIsLoading(true); 

    // save dates to Redux so Back restores them
    dispatch(
      setDates({
        fromDate: dayjs(dateValue).format("YYYY-MM-DD"),
        toDate: dayjs(toDateValue).format("YYYY-MM-DD"),
      })
    );

    const cacheKey = getCacheKey(dayjs(dateValue), dayjs(toDateValue));
    let data = null;

    if (invoicesCache?.[cacheKey]) {
      data = invoicesCache[cacheKey];
    } else {
      data = await fetchInvoices(dayjs(dateValue), dayjs(toDateValue));
      dispatch(saveInvoicesToCache({ key: cacheKey, data }));
    }

    gridRef.current.api.setRowData(data);

    // re-apply saved filter model after refresh
    if (filterModel) {
      gridRef.current.api.setFilterModel(filterModel);
    }
  } 
  catch (err) {
    console.error("Failed to load invoices", err);
  } finally {
    setIsLoading(false); 
  }
  };


  const debouncedLoadInvoices = useDebounce(loadInvoices, 300);

  const onGridReady = async (params) => {
    APIMASTER.UomOptions()
      .then((r) => r.json())
      .then((r) => r.statusCode === 200 && setUoms(r.response));
    APIMASTER.Items()
      .then((r) => r.json())
      .then((r) => {
        if (r.statusCode !== 200) return;
        const itemOptions = r.response.map((e) => ({
          name: e.itemName,
          value: e.id,
          dependentvalue: e.categoryId,
        }));
        setItems(itemOptions);
        setItemList(r.response);
      });
    APISALES.SalesOrderOptions("all")
      .then((r) => r.json())
      .then((r) => setOrders(r.response));
    APIMASTER.SiteCategories()
      .then((r) => r.json())
      .then((r) => r.statusCode === 200 && setSiteCategories(r.response));
    APIMASTER.ItemCategories()
      .then((r) => r.json())
      .then((r) => r.statusCode === 200 && setItemCategories(r.response));
    APIMASTER.SiteOptions()
      .then((r) => r.json())
      .then((r) => r.statusCode === 200 && setSites(r.response));
    APIMASTER.UserOptions()
      .then((r) => r.json())
      .then((r) => r.statusCode === 200 && setUsers(r.response));
    APIMASTER.UomOptions()
      .then((r) => r.json())
      .then((r) => r.statusCode === 200 && setUoms(r.response));

    const d1 = date ? dayjs(date) : dayjs(fromDate || dayjs().startOf("month"));
    const d2 = date
      ? dayjs(toDateValue)
      : dayjs(toDate || dayjs().endOf("month"));

    setDateValue(d1);
    setToDateValue(d2);

    const cacheKey = getCacheKey(d1, d2);
    let data = null;

    if (invoicesCache?.[cacheKey]) {
      data = invoicesCache[cacheKey];
    } else {
      data = await fetchInvoices(d1, d2);
      dispatch(saveInvoicesToCache({ key: cacheKey, data }));
    }

    params.api.setRowData(data);

    if (filterModel) params.api.setFilterModel(filterModel);
  };

  const onFilterChanged = (event) => {
    const model = event.api.getFilterModel();
    dispatch(setFilterModel(model));
  };

  return (
    <SideNav
      currentComponent={CUSTOMER_MANAGEMENT}
      subComponent={INVOICE}
      subComponentPath={PATH_INVOICE}
      detailComponent=""
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row-reverse",
          marginBottom: 10,
          marginRight: 10,
        }}
      >
        <Button
          style={{
            backgroundColor: SPANISH_GREEN,
            textTransform: "capitalize",
            fontSize: 14,
            color: WHITE,
            minWidth: "180px",
            minHeight: "45px",
          }}
          onClick={() => {
            navigate(PATH_INVOICE_DETAIL_WITHOUT_PARAM + 0, {
              state: { date: dayjs(dateValue).format("YYYY-MM-DD") },
            });
          }}
        >
          {ADD}
        </Button>

        <Button
          onClick={deleteModel}
          variant="outlined"
          sx={{ borderColor: SPANISH_GREEN }}
          style={{
            textTransform: "capitalize",
            fontSize: 14,
            color: SPANISH_GREEN,
            minWidth: "180px",
            minHeight: "45px",
            marginRight: 10,
          }}
        >
          {DELETE}
        </Button>
      </div>

      <div
        style={{
          backgroundColor: LIGHT_GREY_BACKGROUND,
          borderRadius: "5px",
          minWidth: "90%",
          minHeight: "50px",
          padding: 20,
          marginBottom: 10,
          borderStyle: "solid",
          border: "1x",
          borderColor: "#eaeaea",
          display: "flex",
        }}
      >
        <div style={{ display: "flex" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              id="dateId"
              label="From Date"
              inputFormat="DD-MM-YYYY"
              value={dateValue}
              onChange={(newValue) => setDateValue(newValue)}
              renderInput={(params) => <TextField {...params} />}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              id="toDateId"
              label="To Date"
              value={toDateValue}
              onChange={(newValue) => setToDateValue(newValue)}
              inputFormat="DD-MM-YYYY"
              renderInput={(params) => (
                <TextField {...params} style={{ marginLeft: 10 }} />
              )}
            />
          </LocalizationProvider>
        </div>
        <div
          align="right"
          style={{
            width: "100%",
            right: 0,
          }}
        >
          <Button
            disabled={isLoading}
            style={{
              backgroundColor: SPANISH_GREEN,
              textTransform: "capitalize",
              fontSize: 14,
              color: WHITE,
              minWidth: "129px",
              minHeight: "45px",
              marginTop: 5,
              opacity: isLoading ? 0.6 : 1,
            }}
            onClick={() => {
              setIsLoading(true);
              debouncedLoadInvoices(dateValue, toDateValue);
            }}
          >
            {GO}
          </Button>
        </div>
      </div>

      <div className="App">
        <div className="ag-theme-alpine" style={{ height: "570px" }}>
          <AgGridReact
            copyGroupHeadersToClipboard={true}
            enableRangeSelection={true}
            columnDefs={columnDefs}
            ref={gridRef}
            defaultColDef={defaultColDef}
            onGridReady={onGridReady}
            paginationAutoPageSize={true}
            pagination={true}
            rowSelection="multiple"
            getRowId={(p) => p.data.id}
            onSelectionChanged={onRowSelectionChanged}
            onFilterChanged={onFilterChanged}
            suppressBrowserResizeObserver={true}
          />
        </div>
      </div>
    </SideNav>
  );
}
